import { useEffect, useState } from "react";
import { Star, X, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await fetch("/api/reviews/");
        const data = await res.json();
        setReviews(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch reviews:", err);
      }
    }
    fetchReviews();
  }, []);

  const openReviewModal = (review) => {
    setSelectedReview(review);
  };

  const closeModal = () => {
    setSelectedReview(null);
  };

  const goToProduct = (productId) => {
    if (productId) {
      navigate(`/product/${productId}`);
      closeModal();
    }
  };

  return (
    <>
      <div className="bg-gray-50 py-20 rounded-3xl shadow-inner relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-10">
            What Our Customers Say
          </h2>

          {reviews.length === 0 ? (
            <p className="text-gray-500">No reviews yet. Be the first!</p>
          ) : (
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex gap-6 animate-scroll hover:[animation-play-state:paused]">
                {reviews.map((r) => (
                  <div
                    key={r.id}
                    onClick={() => openReviewModal(r)}
                    className="min-w-[300px] max-w-[320px] bg-white p-6 rounded-2xl shadow-md border border-gray-100 flex-shrink-0 cursor-pointer hover:scale-105 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {r.user?.[0]?.toUpperCase() || "A"}
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-800">
                          {r.user || "Anonymous"}
                        </p>
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star
                              key={s}
                              className={`w-4 h-4 ${
                                s <= r.rating
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-700 italic text-left line-clamp-3 leading-relaxed">
                      “{r.text || "Great product!"}”
                    </p>

                    <div className="text-sm text-gray-500 text-left mt-4">
                      <p>Product #{r.product || "N/A"}</p>
                      <p>{new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FULLSCREEN REVIEW MODAL */}
      {selectedReview && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>

            {/* User Info */}
            <div className="flex items-center gap-5 mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                {selectedReview.user?.[0]?.toUpperCase() || "A"}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">
                  {selectedReview.user || "Anonymous"}
                </h3>
                <div className="flex gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-6 h-6 ${
                        s <= selectedReview.rating
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Review Text */}
            <p className="text-xl text-gray-700 italic leading-relaxed mb-8 bg-gray-50 p-6 rounded-2xl border-l-4 border-purple-500">
              “{selectedReview.text || "No review text provided."}”
            </p>

            {/* Product Link Button */}
            {selectedReview.product && (
              <button
                onClick={() => goToProduct(selectedReview.product)}
                className="w-full bg-black text-white py-4 px-8 rounded-xl font-semibold text-lg hover:bg-gray-900 transition flex items-center justify-center gap-3 group"
              >
                View This Product
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition" />
              </button>
            )}

            {/* Footer Info */}
            <div className="mt-6 pt-6 border-t text-center text-gray-500 text-sm">
              <p>
                Reviewed on{" "}
                {new Date(selectedReview.created_at).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
              {selectedReview.product && (
                <p className="mt-1 font-medium">Product ID: #{selectedReview.product}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Smooth Scroll Animation */}
      <style>{`
        .animate-scroll {
          display: inline-flex;
          animation: scroll 30s linear infinite;
        }
        @keyframes scroll {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
}
